import React, { useState, useEffect, useCallback } from 'react';
import './StatusTab.css';
import './HealthHourTab.css';
import './NutritionTab.css';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return '';
  return `${m[3]}/${m[2]} ${m[4]}:${m[5]}`;
};

const fmtDateShort = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
};

// "Sexta", "Sábado" etc. — meio-dia fixo no parse pra não virar o dia
// errado por causa do fuso horário.
const fmtWeekday = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  const wk = d.toLocaleDateString('pt-BR', { weekday: 'long' });
  return wk.charAt(0).toUpperCase() + wk.slice(1);
};

const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const MEAL_TYPES = [
  { key: 'cafe_manha', label: 'Café da manhã' },
  { key: 'lanche_manha', label: 'Lanche da manhã' },
  { key: 'almoco', label: 'Almoço' },
  { key: 'lanche_tarde', label: 'Lanche da tarde' },
  { key: 'jantar', label: 'Jantar' },
  { key: 'ceia', label: 'Ceia' },
  { key: 'outro', label: 'Outro' },
];

function NutritionTab({ svc, onFoodLogChanged }) {
  const [plan, setPlan] = useState(null);
  const [geradoEm, setGeradoEm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const [logEntries, setLogEntries] = useState([]);
  const [logMealType, setLogMealType] = useState('almoco');
  const [logDesc, setLogDesc] = useState('');
  const [logDate, setLogDate] = useState(todayISO());
  const [logCalorias, setLogCalorias] = useState('');
  const [logBusy, setLogBusy] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [logError, setLogError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [d, l] = await Promise.all([
        svc.getNutritionPlan(),
        svc.getFoodLog().catch(() => null),
      ]);
      if (d.error) throw new Error(d.error);
      setPlan(d.plan || null);
      setGeradoEm(d.gerado_em || null);
      setLogEntries(l?.entries || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [svc]);

  useEffect(() => { load(); }, [load]);

  const refreshLog = async () => {
    const l = await svc.getFoodLog().catch(() => null);
    setLogEntries(l?.entries || []);
    // Home tem seu próprio card de Refeições com esses mesmos dados —
    // avisa o App pra recarregar sem esperar o usuário sair e voltar da aba.
    onFoodLogChanged?.();
  };

  const addLogEntry = async () => {
    if (!logDesc.trim()) return;
    setLogBusy(true); setLogError(null);
    try {
      const d = await svc.createFoodLog({
        meal_type: logMealType, description: logDesc.trim(),
        log_date: logDate, calorias: logCalorias === '' ? null : logCalorias,
      });
      if (d.error) throw new Error(d.error);
      setLogDesc(''); setLogCalorias('');
      await refreshLog();
    } catch (e) { setLogError(e.message); } finally { setLogBusy(false); }
  };

  const removeLogEntry = async (id) => {
    try { await svc.deleteFoodLog(id); await refreshLog(); }
    catch (e) { setLogError(e.message); }
  };

  const analyzeLogEntry = async (id) => {
    setAnalyzingId(id); setLogError(null);
    try {
      const d = await svc.analyzeFoodLog(id);
      if (d.error) throw new Error(d.error);
      await refreshLog();
    } catch (e) { setLogError(e.message); } finally { setAnalyzingId(null); }
  };

  const removeMeal = async (index) => {
    try {
      const d = await svc.removeNutritionPlanMeal(index);
      if (d.error) throw new Error(d.error);
      setPlan(d.plan || null);
    } catch (e) { setError(e.message); }
  };

  const gerar = async () => {
    setGenerating(true); setError(null);
    try {
      const d = await svc.suggestNutritionPlan();
      if (d.error) throw new Error(d.error);
      setPlan(d.plan || null);
      setGeradoEm(d.gerado_em || null);
    } catch (e) { setError(e.message); } finally { setGenerating(false); }
  };

  if (loading) return <div className="nt-view"><p className="wk-msg">Carregando…</p></div>;

  return (
    <div className="nt-view">
      <div className="nt-head">
        <div className="nt-head-text">
          <h3 className="st-title">🍽️ Cardápio do dia</h3>
          {geradoEm && <span className="nt-gerado-em">Gerado em {fmtDateTime(geradoEm)}</span>}
        </div>
        <button className="wk-btn wk-btn-start nt-gen-btn" onClick={gerar} disabled={generating}>
          {generating ? 'Montando…' : plan ? 'Gerar novo cardápio' : '🤖 Gerar cardápio'}
        </button>
      </div>

      {error && <p className="wk-msg wk-err">{error}</p>}

      {!plan && !generating && (
        <p className="wk-msg">
          Nenhum cardápio gerado ainda. A IA vai olhar seus objetivos, indicadores de
          saúde, composição corporal e treinos recentes pra montar um.
        </p>
      )}

      {plan && (
        <>
          <div className="nt-meta-card">
            <div className="nt-meta-row">
              <div className="nt-meta-stat">
                <span className="nt-meta-label">Calorias</span>
                <span className="nt-meta-value">{plan.meta?.calorias_dia}</span>
              </div>
              <div className="nt-meta-stat">
                <span className="nt-meta-label">Proteína</span>
                <span className="nt-meta-value">{plan.meta?.proteina_g}g</span>
              </div>
              <div className="nt-meta-stat">
                <span className="nt-meta-label">Carboidrato</span>
                <span className="nt-meta-value">{plan.meta?.carboidrato_g}g</span>
              </div>
              <div className="nt-meta-stat">
                <span className="nt-meta-label">Gordura</span>
                <span className="nt-meta-value">{plan.meta?.gordura_g}g</span>
              </div>
            </div>
            {plan.meta?.racional && <p className="nt-racional">{plan.meta.racional}</p>}
          </div>

          <div className="nt-meals">
            {(plan.refeicoes || []).map((r, i) => (
              <div className="nt-meal-card" key={i}>
                <button
                  className="nt-meal-remove"
                  onClick={() => removeMeal(i)}
                  aria-label={`Remover ${r.nome}`}
                >
                  ✕
                </button>
                <div className="nt-meal-head">
                  <span className="nt-meal-name">{r.nome}</span>
                  <span className="nt-meal-time">{r.horario_sugerido}</span>
                </div>
                <ul className="nt-meal-items">
                  {(r.itens || []).map((item, j) => <li key={j}>{item}</li>)}
                </ul>
                {r.calorias != null && <span className="nt-meal-cal">{r.calorias} kcal</span>}
              </div>
            ))}
          </div>

          <p className="nt-disclaimer">
            Sugestão geral por IA, não é aconselhamento médico ou nutricional profissional.
          </p>
        </>
      )}

      {/* ===== Diário alimentar ===== */}
      <div className="st-card">
        <h3 className="st-title">📝 Diário alimentar</h3>
        <p className="st-sub">Registre o que você realmente comeu. A IA pode estimar as calorias a partir da descrição.</p>
        <div className="hh-form">
          <select className="hh-input" value={logMealType} onChange={(e) => setLogMealType(e.target.value)}>
            {MEAL_TYPES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <input
            className="hh-input hh-input-wide" placeholder="O que você comeu? Ex: 2 fatias de pão com ovo e café com leite"
            value={logDesc} onChange={(e) => setLogDesc(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addLogEntry(); }}
          />
          <input className="hh-input" type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} style={{ maxWidth: 150 }} />
          <input
            className="hh-input" type="text" inputMode="numeric" placeholder="kcal (opcional)"
            value={logCalorias} onChange={(e) => setLogCalorias(e.target.value.replace(/\D/g, ''))}
            style={{ maxWidth: 130 }}
          />
          <button className="hh-btn" onClick={addLogEntry} disabled={logBusy || !logDesc.trim()}>
            {logBusy ? '…' : 'Adicionar'}
          </button>
        </div>

        {logError && <p className="wk-msg wk-err">{logError}</p>}

        {logEntries.length === 0 && <p className="wk-msg">Nenhuma refeição registrada ainda.</p>}
        {logEntries.length > 0 && (() => {
          const dias = [];
          const porData = {};
          logEntries.forEach((e) => {
            if (!porData[e.log_date]) {
              porData[e.log_date] = { data: e.log_date, entradas: [] };
              dias.push(porData[e.log_date]);
            }
            porData[e.log_date].entradas.push(e);
          });
          return (
            <div className="nt-log-days">
              {dias.map((dia) => (
                <div className="nt-log-day" key={dia.data}>
                  <div className="nt-log-day-date">
                    {fmtDateShort(dia.data)} {fmtWeekday(dia.data)}
                  </div>
                  {dia.entradas.map((e) => (
                    <div className="nt-log-row" key={e.id}>
                      <button className="nt-log-del" onClick={() => removeLogEntry(e.id)} aria-label="Excluir">✕</button>
                      <span className="nt-log-meal">{e.meal_label}</span>
                      <p className="nt-log-desc">{e.description}</p>
                      <div className="nt-log-bottom">
                        <span className="nt-log-cal">
                          {e.calorias != null ? `${Math.round(e.calorias)} kcal` : 'sem estimativa'}
                          {e.fonte_calorias && <small> ({e.fonte_calorias === 'ia' ? 'IA' : 'manual'})</small>}
                        </span>
                        {e.fonte_calorias !== 'ia' && (
                          <button className="hh-btn nt-log-btn" onClick={() => analyzeLogEntry(e.id)} disabled={analyzingId === e.id}>
                            {analyzingId === e.id ? '…' : '🤖 Analisar com IA'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default NutritionTab;
